/**
 * Chat for the current room (main lobby or the current game). Text is rendered as text, never HTML.
 *
 * Wide screens: a sidebar panel (chat only; game notices pop up as toasts, see `Notices`).
 * Narrower screens: a two-line peek pinned to the bottom that rotates through the last few chat
 * lines (game updates when the room has no chat yet), with a button that opens a half-height sheet: a 'Game updates' section (every
 * notice this room) above the chat. Phones get no toasts at all; the tray is where notices show.
 * On phones the strip also takes any height the game leaves free: when more than two lines fit
 * it stops rotating and shows the latest lines that fit.
 *
 * In a game (not its lobby) the 😀 emote button sits beside the Chat button, or Send on wide screens.
 *
 * Private messages: the 'Send to' picker next to the input lists everyone online (wherever they
 * are); a message to one player goes only to them, highlighted, and stays when you change rooms.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { CHAT_MAX_LENGTH, t, type ChatMessage, type Notice, type PlayerId } from '@kadice/shared';
import type { FeedItem, GameClient } from '../useGameClient';
import { EmotePicker } from './EmotePicker';
import { Highlight } from './Names';
import { VoiceBar } from './VoiceBar';

/** The peek strip rotates through this many recent lines, two at a time. */
const PEEK_POOL = 6;
const PEEK_ROTATE_MS = 5_000;

export function Chat({ client, showDebug }: { client: GameClient; showDebug: boolean }) {
  const [text, setText] = useState('');
  const [to, setTo] = useState<PlayerId | null>(null);
  const recipients = client.online.filter((p) => p.id !== client.me?.id);
  // The picked recipient went offline: back to the room.
  const target = recipients.some((p) => p.id === to) ? to : null;
  const [open, setOpen] = useState(false);
  const logRef = useRef<HTMLOListElement>(null);
  const updatesRef = useRef<HTMLOListElement>(null);
  const title = t(client.view ? 'chat.table' : 'chat.lobby');
  // Debug notices (bot reasoning) only with Debug Mode's Show bot reasoning on.
  const feed = client.feed.filter((f) => !f.notice || f.notice.kind !== 'debug' || showDebug);
  const updates = feed.filter((f): f is FeedItem & { notice: Notice } => !!f.notice);
  // Peek: recent chat if there is any, otherwise recent game updates.
  const chats = feed.filter((f) => f.chat);
  // Lines the strip has room for (more than two only on phones, when the game leaves room).
  const peekRef = useRef<HTMLOListElement>(null);
  const [fits, setFits] = useState(2);
  useEffect(() => {
    const el = peekRef.current;
    if (!el) return;
    const measure = () => {
      const line = parseFloat(getComputedStyle(el).lineHeight) || 16;
      setFits(Math.max(2, Math.floor(el.clientHeight / line)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const tall = fits > 2;
  const pool = (chats.length > 0 ? chats : updates).slice(-(tall ? fits : PEEK_POOL));
  const windows = Math.max(1, pool.length - 1); // consecutive pairs
  const latestId = pool.at(-1)?.id;
  const [peekStart, setPeekStart] = useState(windows - 1);

  // A new line jumps the peek to the latest pair (set during render, not in an effect), then it
  // steps oldest-first and wraps.
  const peekKey = `${latestId}:${windows}:${tall}`;
  const [peekFor, setPeekFor] = useState(peekKey);
  if (peekFor !== peekKey) {
    setPeekFor(peekKey);
    setPeekStart(windows - 1);
  }
  useEffect(() => {
    if (tall || windows < 2) return;
    const timer = setInterval(() => setPeekStart((i) => (i + 1) % windows), PEEK_ROTATE_MS);
    return () => clearInterval(timer);
  }, [latestId, windows, tall]);
  const peek = tall ? pool : pool.slice(Math.min(peekStart, windows - 1), Math.min(peekStart, windows - 1) + 2);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
    updatesRef.current?.scrollTo({ top: updatesRef.current.scrollHeight });
  }, [client.feed, open]);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    const sent = await client.request('chat:send', target === null ? { text } : { text, to: target });
    if (sent !== undefined) setText('');
  };

  const names = [...(client.view?.players.map((p) => p.name) ?? []), client.me?.displayName];
  const chatLine = (m: ChatMessage, key: number) => {
    const mine = m.fromId === client.me?.id;
    const cls = [mine && 'mine', m.toId !== undefined && 'private'].filter(Boolean).join(' ');
    return (
      <li key={key} className={cls || undefined}>
        <span className="from">{m.from}</span>{' '}
        {m.toId !== undefined && (
          <span className="dm-tag">{mine ? t('chat.privateTo', { name: m.to ?? '' }) : t('chat.private')} </span>
        )}
        <Highlight text={m.text} names={names} />
      </li>
    );
  };
  const noticeLine = (n: Notice, key: number) => (
    <li key={key} className={`update ${n.kind}`}>
      <Highlight text={n.text} names={names} />
    </li>
  );
  const emotes = !!client.view && client.view.phase !== 'lobby';
  const feedLine = (f: FeedItem) => (f.chat ? chatLine(f.chat, f.id) : noticeLine(f.notice, f.id));

  return (
    <section className={`chat${open ? ' open' : ''}`}>
      <div className="chat-peek">
        <ol ref={peekRef} className="chat-log" onClick={() => setOpen(true)}>
          {peek.length === 0 ? <li className="muted">{title}</li> : peek.map(feedLine)}
        </ol>
        <button onClick={() => setOpen(true)} aria-label={t('chat.open', { title })}>
          {t('chat.button')}
        </button>
        {emotes && <EmotePicker client={client} />}
      </div>

      <div className="chat-full">
        <div className="row">
          <h3>{title}</h3>
          <span className="spacer" />
          <button className="chat-close" onClick={() => setOpen(false)}>
            {t('common.close')}
          </button>
        </div>
        <div className="chat-updates">
          <h4>{t('chat.updates')}</h4>
          <ol ref={updatesRef} className="chat-log">
            {updates.length === 0 ? (
              <li className="muted">{t('chat.noUpdates')}</li>
            ) : (
              updates.map((f) => noticeLine(f.notice, f.id))
            )}
          </ol>
          <h4>{t('chat.button')}</h4>
        </div>
        <ol ref={logRef} className="chat-log">
          {client.chat.map(chatLine)}
        </ol>
        <form className="inline" onSubmit={(e) => void send(e)}>
          <select
            className="chat-to"
            aria-label={t('chat.toLabel')}
            title={t('chat.toLabel')}
            value={target ?? ''}
            onChange={(e) => setTo(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{t('chat.toEveryone')}</option>
            {recipients.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <input
            value={text}
            maxLength={CHAT_MAX_LENGTH}
            placeholder={t('chat.placeholder')}
            onChange={(e) => setText(e.target.value)}
          />
          <button type="submit">{t('chat.send')}</button>
          {emotes && <EmotePicker client={client} className="emote-in-form" />}
          {client.view && <VoiceBar voice={client.voice} />}
        </form>
      </div>
    </section>
  );
}
